import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button, Form, Input, Modal, Space, Switch, Tabs } from "antd";
import toast from "react-hot-toast";
import { Pencil, Trash2, X } from "lucide-react";
import {
  useCreateExitChecklistTemplateMutation,
  useCreateExitInterviewQuestionSetMutation,
  useDeleteExitChecklistTemplateMutation,
  useDeleteExitInterviewQuestionSetMutation,
  useGetExitChecklistTemplatesQuery,
  useGetExitInterviewQuestionSetsQuery,
  useUpdateExitChecklistTemplateMutation,
  useUpdateExitInterviewQuestionSetMutation,
} from "../../../store/apis/exit.api";
import { useGetAllOrgUsersForFilterQuery } from "../../../store/apis/user.api";
import type {
  ChecklistTemplateDto,
  ChecklistTemplateItemDto,
  CreateChecklistTemplateBody,
  ExitInterviewQuestionDto,
  ExitInterviewQuestionSetDto,
} from "../Utils/types";
import { separationFormRequiredMark } from "../Components/separationFormRequiredMark";
import { separationTextAreaClassName } from "../Utils/separationTextAreaClassName";
import { ExitChecklistDepartment } from "../Utils/types";
import { getErrorMessage } from "../../../store/utils/apiError";
import { useAuth } from "../../../store/hooks/useAuth";
import { useHasPermission } from "../../../store/hooks/useRbac";
import { PERMISSIONS } from "../../../utils/rbac/permissions";
import { ConfigurableTable, Select, ConfirmationModal } from "../../common";
import type { TableColumn } from "../../common/Table";
import SimpleTooltip from "../../common/SimpleTooltip";
import {
  ExitSetupChecklistTemplatesTableSkeleton,
  ExitSetupInterviewTableSkeleton,
} from "../ExitApprovals/components/ExitApprovalsTableSkeletons";

const DEPARTMENT_OPTIONS: { value: string; label: string }[] = [
  { value: ExitChecklistDepartment.IT, label: "IT" },
  { value: ExitChecklistDepartment.ADMIN, label: "Admin" },
  { value: ExitChecklistDepartment.FINANCE, label: "Finance" },
  { value: ExitChecklistDepartment.HR, label: "HR" },
  { value: ExitChecklistDepartment.OTHER, label: "Other" },
];

const INTERVIEW_QUESTION_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: "SHORT_TEXT", label: "Short text" },
  { value: "LONG_TEXT", label: "Long text" },
  { value: "NUMBER", label: "Number" },
  { value: "BOOLEAN", label: "Yes / No" },
  { value: "RATING_1_5", label: "Rating (1–5)" },
  { value: "SINGLE_SELECT", label: "Single choice (list)" },
  { value: "MULTI_SELECT", label: "Multiple choice (list)" },
];

type InterviewQsFormItem = {
  questionKey: string;
  label: string;
  type: string;
  required?: boolean;
  optionsText?: string;
};

function interviewDtoKey(q: ExitInterviewQuestionDto): string {
  return String(q.questionKey ?? q.key ?? "").trim();
}

function interviewDtoLabel(q: ExitInterviewQuestionDto): string {
  return String(q.label ?? q.prompt ?? "").trim();
}

function interviewDtoType(q: ExitInterviewQuestionDto): string {
  return String(q.type ?? q.answerType ?? "SHORT_TEXT");
}

function parseInterviewOptionsText(text: string): string[] {
  return text
    .split(/\n|,/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

function emptyInterviewQuestionRow(): InterviewQsFormItem {
  return {
    questionKey: "",
    label: "",
    type: "SHORT_TEXT",
    required: true,
    optionsText: "",
  };
}

function interviewQuestionsToPayload(
  rows: InterviewQsFormItem[],
): ExitInterviewQuestionDto[] | null {
  if (!rows.length) {
    toast.error("Add at least one question");
    return null;
  }
  const seenKeys = new Set<string>();
  const out: ExitInterviewQuestionDto[] = [];
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i];
    const questionKey = String(r.questionKey ?? "").trim();
    const label = String(r.label ?? "").trim();
    if (!questionKey) {
      toast.error(`Question ${i + 1}: enter a question key (e.g. overall_feedback)`);
      return null;
    }
    if (!label) {
      toast.error(`Question ${i + 1}: enter a label`);
      return null;
    }
    if (seenKeys.has(questionKey)) {
      toast.error(`Duplicate question key: ${questionKey}`);
      return null;
    }
    seenKeys.add(questionKey);
    const typ = String(r.type ?? "SHORT_TEXT");
    const item: ExitInterviewQuestionDto = {
      questionKey,
      label,
      type: typ,
      order: i,
      required: Boolean(r.required),
    };
    if (typ === "SINGLE_SELECT" || typ === "MULTI_SELECT") {
      const opts = parseInterviewOptionsText(String(r.optionsText ?? ""));
      if (opts.length < 1) {
        toast.error(
          `"${label}": add at least one option (one per line or comma-separated)`,
        );
        return null;
      }
      item.options = opts;
    }
    out.push(item);
  }
  return out;
}

export type ExitInterviewQuestionSetsPanelHandle = {
  openCreate: () => void;
};

const ExitInterviewQuestionSetsPanel = forwardRef<
  ExitInterviewQuestionSetsPanelHandle,
  {
    canView: boolean,
    canManage: boolean,
    embeddedInCard?: boolean,
    columnHost?: HTMLDivElement | null,
  }
>(function ExitInterviewQuestionSetsPanel(
  { canView, canManage, embeddedInCard, columnHost },
  ref,
) {
  const { user } = useAuth();
  const sessionReady = Boolean(user?.id);
  const { data: sets = [], isLoading, refetch } =
    useGetExitInterviewQuestionSetsQuery(undefined, {
      skip: !sessionReady || !canView,
    });
  const [delSet, { isLoading: isDeletingSet }] =
    useDeleteExitInterviewQuestionSetMutation();
  const [createSet, { isLoading: isCreatingSet }] =
    useCreateExitInterviewQuestionSetMutation();
  const [updateSet, { isLoading: isUpdatingSet }] =
    useUpdateExitInterviewQuestionSetMutation();
  const [modalOpen, setModalOpen] = useState(false);
  const [delConfirmOpen, setDelConfirmOpen] = useState(false);
  const [setToDel, setSetToDel] = useState<ExitInterviewQuestionSetDto | null>(
    null,
  );
  const [editingSet, setEditingSet] =
    useState<ExitInterviewQuestionSetDto | null>(null);
  const [qsForm] = Form.useForm<{
    name: string;
    questions: InterviewQsFormItem[];
  }>();

  const openCreate = useCallback(() => {
    setEditingSet(null);
    qsForm.resetFields();
    qsForm.setFieldsValue({
      name: "",
      questions: [emptyInterviewQuestionRow()],
    });
    setModalOpen(true);
  }, [qsForm]);

  useImperativeHandle(ref, () => ({ openCreate }), [openCreate]);

  const [interviewTableColumnHost, setInterviewTableColumnHost] =
    useState<HTMLDivElement | null>(null);

  const openEdit = useCallback((row: ExitInterviewQuestionSetDto) => {
    setEditingSet(row);
    qsForm.resetFields();
    const qs = row.questions ?? [];
    const questions: InterviewQsFormItem[] =
      qs.length > 0
        ? qs.map((q) => ({
            questionKey: interviewDtoKey(q),
            label: interviewDtoLabel(q),
            type: interviewDtoType(q),
            required: Boolean(q.required),
            optionsText: Array.isArray(q.options)
              ? q.options.join("\n")
              : "",
          }))
        : [emptyInterviewQuestionRow()];
    qsForm.setFieldsValue({
      name: row.name ?? "",
      questions,
    });
    setModalOpen(true);
  }, [qsForm]);

  const questionSetColumns = useMemo<TableColumn<ExitInterviewQuestionSetDto>[]>(() => {
    const cols: TableColumn<ExitInterviewQuestionSetDto>[] = [
      {
        key: "name",
        title: "Name",
        label: "Name",
        dataIndex: "name",
        required: true,
      },
      {
        key: "questions",
        title: "Questions",
        label: "Questions",
        render: (_, r) =>
          Array.isArray(r.questions) ? r.questions.length : "—",
      },
    ];
    if (canManage) {
      const setupActionIconBtn =
        "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-slate-600 transition-colors hover:bg-slate-100";
      cols.push({
        key: "actions",
        title: "Actions",
        label: "Actions",
        required: true,
        width: 96,
        align: "center",
        truncate: false,
        render: (_, r) => (
          <div className="inline-flex items-center justify-center gap-1.5 flex-nowrap">
            <SimpleTooltip
              label="Edit"
              side="top"
              className="inline-flex shrink-0"
              tooltipClassName="text-xs shadow-md border-0"
            >
              <button
                type="button"
                onClick={() => openEdit(r)}
                aria-label="Edit"
                className={setupActionIconBtn}
              >
                <Pencil
                  className="w-4 h-4 text-primary-600"
                  aria-hidden
                />
              </button>
            </SimpleTooltip>
            <SimpleTooltip
              label="Delete"
              side="top"
              className="inline-flex shrink-0"
              tooltipClassName="text-xs shadow-md border-0"
            >
              <button
                type="button"
                aria-label="Delete"
                onClick={() => {
                  setSetToDel(r);
                  setDelConfirmOpen(true);
                }}
                className={`${setupActionIconBtn} hover:text-red-700`}
              >
                <Trash2 className="w-4 h-4 text-red-600" aria-hidden />
              </button>
            </SimpleTooltip>
          </div>
        ),
      });
    }
    return cols;
  }, [canManage, openEdit, delSet, refetch]);

  const submitQsModal = async () => {
    try {
      const v = await qsForm.validateFields();
      const questions = interviewQuestionsToPayload(v.questions ?? []);
      if (!questions) return;
      if (editingSet) {
        await updateSet({
          setId: editingSet.id,
          body: { name: v.name.trim(), questions },
        }).unwrap();
        toast.success("Exit interview question set updated successfully");
      } else {
        await createSet({
          name: v.name.trim(),
          questions,
        }).unwrap();
        toast.success("Exit interview question set created successfully");
      }
      setModalOpen(false);
      setEditingSet(null);
      qsForm.resetFields();
      void refetch();
    } catch (e) {
      if (e && typeof e === "object" && "errorFields" in e) return;
      toast.error(getErrorMessage(e as never) ?? "Save failed");
    }
  };

  if (embeddedInCard) {
    return (
      <>
        <ConfigurableTable<ExitInterviewQuestionSetDto>
          columns={questionSetColumns}
          data={sets}
          loading={isLoading}
          skeleton={
            <ExitSetupInterviewTableSkeleton
              rows={5}
              hasActions={canManage}
            />
          }
          rowKey="id"
          emptyMessage="No interview question sets yet."
          bordered={false}
          hoverable
          size="sm"
          spacing={0}
          className="min-w-0 [&_thead_th]:!text-slate-500 [&_th:first-child]:!pl-4 [&_td:first-child]:!pl-4 [&_th:last-child]:!pr-4 [&_td:last-child]:!pr-4"
          columnPickerPortal
          columnPortalHost={columnHost}
          configOptions={{
            persistenceKey: "separation-exit-interview-question-sets",
          }}
        />

        <Modal
          centered
          transitionName=""
          maskTransitionName=""
          title={
            editingSet
              ? "Edit interview question set"
              : "New interview question set"
          }
          open={modalOpen}
          onCancel={() => {
            setModalOpen(false);
            setEditingSet(null);
          }}
          onOk={() => void submitQsModal()}
          confirmLoading={isCreatingSet || isUpdatingSet}
          okText="Save"
          width={720}
          destroyOnClose
        >
          <Form
            form={qsForm}
            layout="vertical"
            requiredMark={separationFormRequiredMark}
          >
            <Form.Item
              name="name"
              label="Name"
              rules={[{ required: true, message: "Required" }]}
            >
              <Input placeholder="Set name" />
            </Form.Item>
            <Form.List name="questions">
              {(fields, { add, remove }) => (
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-slate-700">Questions</span>
                    <Button
                      type="dashed"
                      size="small"
                      onClick={() => add(emptyInterviewQuestionRow())}
                    >
                      Add question
                    </Button>
                  </div>
                  <div className="max-h-[50vh] overflow-y-auto pr-1 -mr-1 space-y-3">
                    {fields.map((field, index) => (
                      <div
                        key={field.key}
                        className="border border-slate-200 rounded-lg p-3 space-y-2"
                      >
                        <div className="flex justify-between items-start gap-2">
                          <span className="text-sm text-slate-500">
                            Question {index + 1}
                          </span>
                          {fields.length > 1 ? (
                            <SimpleTooltip
                              label="Remove question"
                              side="top"
                              className="inline-block"
                              tooltipClassName="text-xs shadow-md border-0"
                            >
                              <button
                                type="button"
                                onClick={() => remove(field.name)}
                                aria-label="Remove question"
                                className="text-slate-500 hover:text-red-600 transition-colors rounded p-0.5"
                              >
                                <X className="w-4 h-4" aria-hidden />
                              </button>
                            </SimpleTooltip>
                          ) : null}
                        </div>
                        <Form.Item
                          {...field}
                          name={[field.name, "questionKey"]}
                          label="Question key (internal name)"
                          extra="Unique identifier (e.g. feedback_on_culture)"
                          rules={[{ required: true, message: "Required" }]}
                        >
                          <Input placeholder="question_key" />
                        </Form.Item>
                        <Form.Item
                          {...field}
                          name={[field.name, "label"]}
                          label="Label / Question prompt"
                          rules={[{ required: true, message: "Required" }]}
                        >
                          <Input placeholder="How would you rate..." />
                        </Form.Item>
                        <Form.Item
                          {...field}
                          name={[field.name, "type"]}
                          label="Answer type"
                          rules={[{ required: true }]}
                          getValueFromEvent={(v) => v}
                        >
                          <Select
                            className="w-full"
                            options={INTERVIEW_QUESTION_TYPE_OPTIONS}
                            searchable
                          />
                        </Form.Item>
                        <Form.Item
                          noStyle
                          shouldUpdate={(p, c) =>
                            p.questions[index]?.type !== c.questions[index]?.type
                          }
                        >
                          {({ getFieldValue }) => {
                            const typ = getFieldValue([
                              "questions",
                              index,
                              "type",
                            ]);
                            if (
                              typ !== "SINGLE_SELECT" &&
                              typ !== "MULTI_SELECT"
                            ) {
                              return null;
                            }
                            return (
                              <Form.Item
                                {...field}
                                name={[field.name, "optionsText"]}
                                label="Options"
                                extra="One per line or comma-separated."
                                rules={[
                                  {
                                    required: true,
                                    message: "Add at least one option",
                                  },
                                ]}
                              >
                                <Input.TextArea
                                  className={separationTextAreaClassName}
                                  rows={3}
                                  placeholder={"Option A\nOption B"}
                                />
                              </Form.Item>
                            );
                          }}
                        </Form.Item>
                        <Form.Item
                          {...field}
                          name={[field.name, "required"]}
                          label="Required"
                          valuePropName="checked"
                        >
                          <Switch />
                        </Form.Item>
                      </div>
                    ))}
                  </div>
                </div>
            )}
          </Form.List>
        </Form>
        </Modal>

        <ConfirmationModal
          isOpen={delConfirmOpen}
          onClose={() => {
            setDelConfirmOpen(false);
            setSetToDel(null);
          }}
          onConfirm={async () => {
            if (!setToDel) return;
            try {
              await delSet(setToDel.id).unwrap();
              toast.success("Question set has been deleted successfully");
              setDelConfirmOpen(false);
              setSetToDel(null);
              void refetch();
            } catch (e) {
              toast.error(getErrorMessage(e as never) ?? "Could not delete the interview question set");
            }
          }}
          title="Delete question set?"
          message={`Are you sure you want to delete "${setToDel?.name ?? setToDel?.id}"? This action cannot be undone.`}
          type="danger"
          confirmText="Delete set"
          isLoading={isDeletingSet}
        />
      </>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-6 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-lg font-bold text-slate-900 m-0">
            Interview question sets ({sets.length})
          </h3>
          <div className="flex flex-wrap items-center justify-end gap-2 shrink-0">
            {canManage ? (
              <Button type="primary" onClick={openCreate}>
                New question set
              </Button>
            ) : null}
            <div
              ref={setInterviewTableColumnHost}
              className="shrink-0 flex items-center min-h-[40px]"
            />
          </div>
        </div>
        <ConfigurableTable<ExitInterviewQuestionSetDto>
          columns={questionSetColumns}
          data={sets}
          loading={isLoading}
          skeleton={
            <ExitSetupInterviewTableSkeleton
              rows={5}
              hasActions={canManage}
            />
          }
          rowKey="id"
          emptyMessage="No interview question sets yet."
          bordered={false}
          hoverable
          size="sm"
          spacing={0}
          className="min-w-0 [&_thead_th]:!text-slate-500 [&_th:first-child]:!pl-4 [&_td:first-child]:!pl-4 [&_th:last-child]:!pr-4 [&_td:last-child]:!pr-4"
          columnPickerPortal
          columnPortalHost={interviewTableColumnHost}
          configOptions={{
            persistenceKey: "separation-exit-interview-question-sets",
          }}
        />
      </div>

      <Modal
        centered
        transitionName=""
        maskTransitionName=""
        title={editingSet ? "Edit interview question set" : "New interview question set"}
        open={modalOpen}
        onCancel={() => {
          setModalOpen(false);
          setEditingSet(null);
        }}
        onOk={() => void submitQsModal()}
        confirmLoading={isCreatingSet || isUpdatingSet}
        okText="Save"
        width={720}
        destroyOnClose
      >
        <Form
          form={qsForm}
          layout="vertical"
          className="mt-2"
          requiredMark={separationFormRequiredMark}
        >
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: "Required" }]}
          >
            <Input placeholder="Set name" />
          </Form.Item>
          <Form.List name="questions">
            {(fields, { add, remove }) => (
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-slate-700">Questions</span>
                  <Button
                    type="dashed"
                    size="small"
                    onClick={() => add(emptyInterviewQuestionRow())}
                  >
                    Add question
                  </Button>
                </div>
                {fields.map((field, index) => (
                  <div
                    key={field.key}
                    className="border border-slate-200 rounded-lg p-3 space-y-2"
                  >
                    <div className="flex justify-between items-start gap-2">
                      <span className="text-sm text-slate-500">
                        Question {index + 1}
                      </span>
                      {fields.length > 1 ? (
                        <SimpleTooltip
                          label="Remove question"
                          side="top"
                          className="inline-block"
                          tooltipClassName="text-xs shadow-md border-0"
                        >
                          <button
                            type="button"
                            onClick={() => remove(field.name)}
                            aria-label="Remove question"
                            className="text-slate-500 hover:text-red-600 transition-colors rounded p-0.5"
                          >
                            <X className="w-4 h-4" aria-hidden />
                          </button>
                        </SimpleTooltip>
                      ) : null}
                    </div>
                    <Form.Item
                      {...field}
                      name={[field.name, "questionKey"]}
                      label=""
                      rules={[{ required: true, message: "Required" }]}
                      extra=""
                    >
                      <Input placeholder="e.g. overall_feedback" />
                    </Form.Item>
                    <Form.Item
                      {...field}
                      name={[field.name, "label"]}
                      label="Label"
                      rules={[{ required: true, message: "Required" }]}
                    >
                      <Input placeholder="Text shown to the employee" />
                    </Form.Item>
                    <Form.Item
                      {...field}
                      name={[field.name, "type"]}
                      label="Type"
                      rules={[{ required: true, message: "Required" }]}
                      getValueFromEvent={(v) => v}
                    >
                      <Select
                        className="w-full"
                        options={INTERVIEW_QUESTION_TYPE_OPTIONS}
                        searchable
                      />
                    </Form.Item>
                    <Form.Item
                      noStyle
                      shouldUpdate={(prev, cur) => {
                        const pi = prev?.questions?.[field.name]?.type;
                        const ci = cur?.questions?.[field.name]?.type;
                        return pi !== ci;
                      }}
                    >
                      {() => {
                        const typ = qsForm.getFieldValue([
                          "questions",
                          field.name,
                          "type",
                        ]) as string;
                        if (typ !== "SINGLE_SELECT" && typ !== "MULTI_SELECT") {
                          return null;
                        }
                        return (
                          <Form.Item
                            {...field}
                            name={[field.name, "optionsText"]}
                            label="Options"
                            extra="One per line or comma-separated."
                            rules={[
                              {
                                required: true,
                                message: "Add at least one option",
                              },
                            ]}
                          >
                            <Input.TextArea
                              className={separationTextAreaClassName}
                              rows={3}
                              placeholder={"Option A\nOption B"}
                            />
                          </Form.Item>
                        );
                      }}
                    </Form.Item>
                    <Form.Item
                      {...field}
                      name={[field.name, "required"]}
                      label="Required"
                      valuePropName="checked"
                    >
                      <Switch />
                    </Form.Item>
                  </div>
                ))}
              </div>
            )}
          </Form.List>
        </Form>
      </Modal>
    </div>
  );
});

const ChecklistTemplatesPage: React.FC<{ embedded?: boolean }> = ({
  embedded = false,
}) => {
  const { user } = useAuth();
  const sessionReady = Boolean(user?.id);
  const canView = useHasPermission(PERMISSIONS.EXIT_APPROVALS_VIEW);
  const canManage = useHasPermission(PERMISSIONS.EXIT_APPROVALS_MANAGE);

  const [deptFilter, setDeptFilter] = useState<string | undefined>();
  const [setupTab, setSetupTab] = useState<string>("templates");
  const [checklistTemplatesColumnHost, setChecklistTemplatesColumnHost] =
    useState<HTMLDivElement | null>(null);
  const [interviewTableColumnHost, setInterviewTableColumnHost] =
    useState<HTMLDivElement | null>(null);
  const interviewPanelRef =
    useRef<ExitInterviewQuestionSetsPanelHandle>(null);
  const { data: templates = [], isLoading, refetch } =
    useGetExitChecklistTemplatesQuery(
      deptFilter ? { department: deptFilter } : undefined,
      { skip: !sessionReady || !canView },
    );
  const { data: orgUsers = [] } = useGetAllOrgUsersForFilterQuery(undefined, {
    skip: !sessionReady || !canView || !canManage,
  });
  const [createTpl, { isLoading: isCreatingTpl }] =
    useCreateExitChecklistTemplateMutation();
  const [updateTpl, { isLoading: isUpdatingTpl }] =
    useUpdateExitChecklistTemplateMutation();
  const [deleteTpl, { isLoading: isDeletingTpl }] =
    useDeleteExitChecklistTemplateMutation();

  const [modalOpen, setModalOpen] = useState(false);
  const [delConfirmOpen, setDelConfirmOpen] = useState(false);
  const [tplToDel, setTplToDel] = useState<ChecklistTemplateDto | null>(null);
  const [editing, setEditing] = useState<ChecklistTemplateDto | null>(null);
  const [form] = Form.useForm<CreateChecklistTemplateBody>();

  const userOptions = useMemo(
    () =>
      orgUsers.map((u) => ({
        value: u.id,
        label: `${u.fullName}${u.empCode ? ` (${u.empCode})` : ""}`,
      })),
    [orgUsers],
  );

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      name: "",
      department: ExitChecklistDepartment.IT,
      items: [
        {
          taskName: "",
          department: ExitChecklistDepartment.IT,
          isMandatory: true,
        },
      ],
    });
    setModalOpen(true);
  };

  const openEdit = useCallback((row: ChecklistTemplateDto) => {
    setEditing(row);
    form.setFieldsValue({
      name: row.name,
      department: row.department as CreateChecklistTemplateBody["department"],
      items: row.items?.length
        ? row.items.map((i) => ({
            taskName: i.taskName,
            department: i.department as CreateChecklistTemplateBody["department"],
            isMandatory: i.isMandatory ?? false,
            defaultAssigneeUserId: i.defaultAssigneeUserId,
          }))
        : [
            {
              taskName: "",
              department:
                row.department as CreateChecklistTemplateBody["department"],
              isMandatory: true,
            },
          ],
    });
    setModalOpen(true);
  }, [form]);

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    form.resetFields();
  };

  const submitModal = async () => {
    try {
      const values = await form.validateFields();
      const items = (values.items ?? []).map((i: ChecklistTemplateItemDto) => ({
        taskName: i.taskName?.trim(),
        department: i.department,
        isMandatory: Boolean(i.isMandatory),
        defaultAssigneeUserId: i.defaultAssigneeUserId || undefined,
      }));
      for (const it of items) {
        if (!it.taskName) {
          toast.error("Each item needs a task name");
          return;
        }
        if (!it.defaultAssigneeUserId) {
          toast.error(
            "Each template line needs a default assignee (required for apply-template)",
          );
          return;
        }
      }
      const body: CreateChecklistTemplateBody = {
        name: values.name.trim(),
        department: values.department,
        items,
      };
      if (editing) {
        await updateTpl({ templateId: editing.id, body }).unwrap();
        toast.success("Checklist template updated successfully");
      } else {
        await createTpl(body).unwrap();
        toast.success("Checklist template created successfully");
      }
      closeModal();
      void refetch();
    } catch (e) {
      if (e && typeof e === "object" && "errorFields" in e) return;
      toast.error(getErrorMessage(e as never) ?? "Could not save the checklist template");
    }
  };

  const onDelete = useCallback((row: ChecklistTemplateDto) => {
    setTplToDel(row);
    setDelConfirmOpen(true);
  }, []);

  const checklistTemplateColumns = useMemo<TableColumn<ChecklistTemplateDto>[]>(() => {
    const cols: TableColumn<ChecklistTemplateDto>[] = [
      {
        key: "name",
        title: "Name",
        label: "Name",
        dataIndex: "name",
        required: true,
      },
      {
        key: "department",
        title: "Department",
        label: "Department",
        dataIndex: "department",
        render: (d) =>
          d != null && String(d) !== "" ? String(d) : "—",
      },
      {
        key: "items",
        title: "Items",
        label: "Items",
        render: (_, r) => r.items?.length ?? 0,
      },
    ];
    if (canManage) {
      const setupActionIconBtn =
        "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-slate-600 transition-colors hover:bg-slate-100";
      cols.push({
        key: "actions",
        title: "Actions",
        label: "Actions",
        required: true,
        width: 96,
        align: "center",
        truncate: false,
        render: (_, r) => (
          <div className="inline-flex items-center justify-center gap-1.5 flex-nowrap">
            <SimpleTooltip
              label="Edit"
              side="top"
              className="inline-flex shrink-0"
              tooltipClassName="text-xs shadow-md border-0"
            >
              <button
                type="button"
                onClick={() => openEdit(r)}
                aria-label="Edit"
                className={setupActionIconBtn}
              >
                <Pencil
                  className="w-4 h-4 text-primary-600"
                  aria-hidden
                />
              </button>
            </SimpleTooltip>
            <SimpleTooltip
              label="Delete"
              side="top"
              className="inline-flex shrink-0"
              tooltipClassName="text-xs shadow-md border-0"
            >
              <button
                type="button"
                onClick={() => onDelete(r)}
                aria-label="Delete"
                className={`${setupActionIconBtn} hover:text-red-700`}
              >
                <Trash2
                  className="w-4 h-4 text-red-600"
                  aria-hidden
                />
              </button>
            </SimpleTooltip>
          </div>
        ),
      });
    }
    return cols;
  }, [canManage, openEdit, onDelete]);

  if (!sessionReady || !canView) {
    return (
      <div className="p-4">
        <p className="text-slate-600">You do not have access to this page.</p>
      </div>
    );
  }

  return (
    <div
      className={
        embedded
          ? "w-full min-w-0 flex flex-col gap-6"
          : "p-4 max-w-5xl flex flex-col gap-6"
      }
    >
      {!embedded ? (
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 m-0">
          Exit checklist &amp; interview setup
        </h1>
      ) : null}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden flex flex-col min-h-[400px]">
        {/* Integrated Header with Tabs */}
        <div className="px-4 py-6 border-b border-slate-200 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-1 bg-slate-100/80 p-1 rounded-lg">
            <button
              type="button"
              onClick={() => setSetupTab("templates")}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
                setupTab === "templates"
                  ? "bg-white text-primary-600 shadow-sm"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              Checklist templates
            </button>
            <button
              type="button"
              onClick={() => setSetupTab("interview")}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
                setupTab === "interview"
                  ? "bg-white text-primary-600 shadow-sm"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              Interview question sets
            </button>
          </div>

          <div className="flex flex-wrap items-center justify-end gap-2 shrink-0">
            {setupTab === "templates" ? (
              <>
                {canManage ? (
                  <Button type="primary" onClick={openCreate}>
                    New template
                  </Button>
                ) : null}
                <div
                  ref={setChecklistTemplatesColumnHost}
                  className="shrink-0 flex items-center min-h-[40px]"
                />
              </>
            ) : (
              <>
                {canManage ? (
                  <Button
                    type="primary"
                    onClick={() => interviewPanelRef.current?.openCreate()}
                  >
                    New question set
                  </Button>
                ) : null}
                <div
                  ref={setInterviewTableColumnHost}
                  className="shrink-0 flex items-center min-h-[40px]"
                />
              </>
            )}
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 w-full overflow-hidden p-0">
          {setupTab === "templates" ? (
            <ConfigurableTable<ChecklistTemplateDto>
              columns={checklistTemplateColumns}
              data={templates}
              loading={isLoading}
              skeleton={
                <ExitSetupChecklistTemplatesTableSkeleton
                  rows={5}
                  hasActions={canManage}
                />
              }
              rowKey="id"
              emptyMessage="No checklist templates yet."
              bordered={false}
              hoverable
              size="sm"
              spacing={0}
              className="min-w-0 [&_thead_th]:!text-slate-500 [&_th:first-child]:!pl-4 [&_td:first-child]:!pl-4 [&_th:last-child]:!pr-4 [&_td:last-child]:!pr-4"
              columnPickerPortal
              columnPortalHost={checklistTemplatesColumnHost}
              configOptions={{
                persistenceKey: "separation-exit-checklist-templates",
              }}
            />
          ) : (
            <div className="w-full">
              <ExitInterviewQuestionSetsPanel
                ref={interviewPanelRef}
                canView={canView}
                canManage={canManage}
                embeddedInCard
                columnHost={interviewTableColumnHost}
              />
            </div>
          )}
        </div>
      </div>

      <Modal
        centered
        transitionName=""
        maskTransitionName=""
        title={editing ? "Edit template" : "New template"}
        open={modalOpen}
        confirmLoading={isCreatingTpl || isUpdatingTpl}
        onCancel={closeModal}
        width={720}
        onOk={() => void submitModal()}
        okText="Save"
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          requiredMark={separationFormRequiredMark}
        >
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: "Required" }]}
          >
            <Input placeholder="Template name" />
          </Form.Item>
          <Form.Item
            name="department"
            label="Department"
            rules={[{ required: true }]}
            getValueFromEvent={(v) => v}
          >
            <Select
              className="w-full"
              options={DEPARTMENT_OPTIONS}
              searchable
            />
          </Form.Item>
          <Form.List name="items">
            {(fields, { add, remove }) => (
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-slate-700">Tasks</span>
                  <Button type="dashed" size="small" onClick={() => add()}>
                    Add Task
                  </Button>
                </div>
                <div className="max-h-[50vh] overflow-y-auto pr-1 -mr-1 space-y-3">
                  {fields.map((field, index) => (
                    <div
                      key={field.key}
                      className="border border-slate-200 rounded-lg p-3 space-y-2"
                    >
                      <div className="flex justify-between">
                        <span className="text-sm text-slate-500">
                          Task {index + 1}
                        </span>
                        {fields.length > 1 ? (
                          <SimpleTooltip
                            label="Remove task"
                            side="top"
                            className="inline-block"
                            tooltipClassName="text-xs shadow-md border-0"
                          >
                            <button
                              type="button"
                              onClick={() => remove(field.name)}
                              aria-label="Remove task"
                              className="text-slate-500 hover:text-red-600 transition-colors rounded p-0.5"
                            >
                              <X className="w-4 h-4" aria-hidden />
                            </button>
                          </SimpleTooltip>
                        ) : null}
                      </div>
                      <Form.Item
                        {...field}
                        name={[field.name, "taskName"]}
                        label=""
                        rules={[{ required: true, message: "Required" }]}
                      >
                        <Input placeholder="Task name" />
                      </Form.Item>
                      <Form.Item
                        {...field}
                        name={[field.name, "department"]}
                        label="Task department"
                        rules={[{ required: true }]}
                        getValueFromEvent={(v) => v}
                      >
                        <Select
                          className="w-full"
                          options={DEPARTMENT_OPTIONS}
                          searchable
                        />
                      </Form.Item>
                      <Space wrap className="w-full">
                        <Form.Item
                          {...field}
                          name={[field.name, "defaultAssigneeUserId"]}
                          label="Default assignee"
                          rules={[{ required: true, message: "Required for apply" }]}
                          className="min-w-[240px]"
                          getValueFromEvent={(v) => v || undefined}
                        >
                          <Select
                            searchable
                            placeholder="User"
                            className="w-full min-w-[220px]"
                            options={userOptions}
                            clearable
                          />
                        </Form.Item>
                        <Form.Item
                          {...field}
                          name={[field.name, "isMandatory"]}
                          label="Mandatory"
                          valuePropName="checked"
                          initialValue
                        >
                          <Switch />
                        </Form.Item>
                      </Space>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Form.List>
        </Form>
      </Modal>

      <ConfirmationModal
        isOpen={delConfirmOpen}
        onClose={() => {
          setDelConfirmOpen(false);
          setTplToDel(null);
        }}
        onConfirm={async () => {
          if (!tplToDel) return;
          try {
            await deleteTpl(tplToDel.id).unwrap();
            toast.success("Checklist template has been deleted successfully");
            setDelConfirmOpen(false);
            setTplToDel(null);
            void refetch();
          } catch (e) {
            toast.error(getErrorMessage(e as never) ?? "Could not delete the checklist template");
          }
        }}
        title="Delete template?"
        message={`Are you sure you want to delete "${tplToDel?.name}"? This action cannot be undone.`}
        type="danger"
        confirmText="Delete template"
        isLoading={isDeletingTpl}
      />
    </div>
  );
};

export default ChecklistTemplatesPage;
